import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import { styled } from "@mui/material";
import Grid from "@mui/material/Grid";
import { CanceledError } from "axios";
import IndividualAPIService from "marketplace/entities/individual/api/individual-api-service";
import Individual from "marketplace/entities/individual/individual";
import { enqueueSnackbar } from "notistack";
import React, { useEffect } from "react";
import { useSession } from "users/session/session-context";
import WorkAgreement, { Masked, WorkAgreementFieldCategory } from "work/entities/work-agreement/work-agreement";
import VendorTeamReadOnly from "work/values/vendor-team/view/vendor-team-readonly";
import { HeadControls } from "work/entities/views/work-dialog-shared-components";

const ListContainer = styled(Grid)(
  () => ({
    minHeight: "20rem",
  }));
const TeamReview = styled(VendorTeamReadOnly)(
  ({ theme }) => ({
    [theme.breakpoints.down("lg")]: {
      marginTop: theme.spacing(2),
    },
    marginLeft: theme.spacing(2),
  }));

type TeamTabProps = {
  activeTab: WorkAgreementFieldCategory;
  workAgreement: WorkAgreement;
};

export default function TeamTab(props: Readonly<TeamTabProps>) {
  const {
    activeTab,
    workAgreement
  } = props;

  const [selectedLeader, setSelectedLeader] = React.useState<Individual | null>(null);
  const [selectedMembers, setSelectedMembers] = React.useState<Individual[]>([]);

  const session = useSession();

  useEffect(
    () => {
      const fetchTeamMembers = async () => {
        if (Masked.isMasked(workAgreement.team)) return;
        if (activeTab !== WorkAgreementFieldCategory.Team) return null;
        if (!workAgreement.team?.leader) return;

        try {
          const individualService = new IndividualAPIService(session);
          const individualIdsToFetch = [
            workAgreement.team.leader.userId,
            ...workAgreement.team.members.map((member) => member.userId)
          ];

          let abortController = new AbortController();
          const response = await individualService.getUsersProfileInfo(
            individualIdsToFetch,
            abortController
          );

          const leader = response.find((ind: Individual) =>
          (Masked.isNotMasked(workAgreement.team) &&
            ind.userId.isEqualTo(workAgreement.team?.leader?.userId))) ??
            null;

          setSelectedLeader(leader);

          const members = response.filter((ind: Individual) =>
          (Masked.isNotMasked(workAgreement.team) &&
            !ind.userId.isEqualTo(workAgreement.team?.leader?.userId))) ??
            [];
          setSelectedMembers(members);

          return () => {
            abortController.abort();
            abortController = new AbortController();
          };

        } catch (error) {
          if (!(error instanceof CanceledError)) {
            enqueueSnackbar(
              "Failed to load vendor-team member information",
              { variant: "error" }
            );
          }
        }
      };

      void fetchTeamMembers();
    },
    [activeTab]
  );

  if (activeTab !== WorkAgreementFieldCategory.Team) return null;
  if (Masked.isMasked(workAgreement.team) ||
    Masked.isMasked(workAgreement.teamRestricted)) {
    return "You do not have permission to view the team for this work agreement.";
  }

  return (
    <>
      <HeadControls>
        <FormControlLabel
          checked={workAgreement.teamRestricted.value}
          control={
            <Switch
              disabled={true}
              color="primary"
            />
          }
          label={
            workAgreement.teamRestricted.value ? (
              <strong>Team Restricted</strong>
            ) : (
              "Team Not Restricted"
            )
          }
        />
      </HeadControls>
      <ListContainer container direction="row">
        <Grid size="grow">
          <TeamReview
            leader={selectedLeader}
            members={selectedMembers}
            hideCommentButton={true}
          />
        </Grid>
      </ListContainer>
    </>
  );
}
